/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// src/data/productCatalog.ts

/**
 * The canonical list of Deskshare product slugs (A-11).
 *
 * This list previously existed only inside the sitemap build plugin, where the
 * running site could not read it. Two consumers now need the same answer:
 *
 *  1. The sitemap generator, at build time.
 *  2. Download conversion tracking, at run time — it has to know whether a URL
 *     path names a real product before it reports a conversion for it.
 *
 * Keeping one list means a new product cannot appear in the sitemap while being
 * invisible to conversion reporting, or the reverse.
 *
 * IMPORTANT — this file is imported by `vite.config.ts` through the sitemap
 * plugin, so it must stay free of browser globals, React, and
 * `import.meta.env`. Plain data and pure functions only.
 *
 * Slugs were confirmed against the live site during Session 305. Earlier
 * feedback documents quoted `screenrecorder` and `dictation-pro`; neither is
 * real, and neither belongs here.
 */

export interface ProductCatalogEntry {
  /** URL segment for the product page: /{lang}/{slug} */
  slug: string;
  /**
   * The product page's real "last changed" date, in YYYY-MM-DD.
   *
   * Bump it ONLY when a product's page content meaningfully changes. Do NOT use
   * the build date: stamping every product with today's date on each deploy
   * tells search engines that every page changed on every deploy, which erodes
   * trust in the dates and in the crawl signal. A stable per-product date keeps
   * that signal meaningful.
   */
  lastmod: string;
}

/** Business-preferred display order. The sitemap emits entries in this order. */
export const PRODUCT_CATALOG = [
  { slug: "dictation", lastmod: "2026-05-09" },
  { slug: "document-translator", lastmod: "2026-05-09" },
  { slug: "ftp-client", lastmod: "2026-05-09" },
  { slug: "ftp-manager-free", lastmod: "2026-05-09" },
  { slug: "wcm", lastmod: "2026-05-09" },
  { slug: "ip-camera-viewer", lastmod: "2026-05-09" },
  { slug: "paper-scanner-pro", lastmod: "2026-05-09" },
  { slug: "screen-recorder", lastmod: "2026-05-09" },
  { slug: "text-to-speech-software", lastmod: "2026-05-09" },
  { slug: "video-surveillance-software", lastmod: "2026-05-09" },
] as const satisfies readonly ProductCatalogEntry[];

/**
 * V-3e: the ten product names, as a type rather than as plain text.
 *
 * `satisfies` above keeps the shape checked while preserving the exact names,
 * so this type is derived from the catalogue itself and can never drift from
 * it. Anything keyed on ProductSlug — the advertising conversion registry, for
 * one — then fails to build if it uses a name this site does not serve, instead
 * of failing silently at runtime and reporting nothing for that product.
 */
export type ProductSlug = (typeof PRODUCT_CATALOG)[number]["slug"];

/** Slugs only, in catalogue order. */
export const PRODUCT_SLUGS: readonly ProductSlug[] = PRODUCT_CATALOG.map(
  (entry) => entry.slug,
);

const PRODUCT_SLUG_SET: ReadonlySet<string> = new Set(PRODUCT_SLUGS);

/**
 * Whether `slug` names a product we actually sell.
 *
 * Used before reporting a conversion so a typo, a stale marketing link or a
 * crawler probing invented paths cannot create a product dimension in Google
 * Ads that no report will ever be able to explain.
 */
export function isKnownProductSlug(slug: string | undefined | null): boolean {
  return typeof slug === "string" && PRODUCT_SLUG_SET.has(slug);
}

/**
 * Pull the product slug out of a URL path, or return undefined.
 *
 * Handles every shape the router produces:
 *   /screen-recorder                 → screen-recorder
 *   /de/screen-recorder              → screen-recorder
 *   /de/screen-recorder/reviews      → screen-recorder
 *   /de/screen-recorder/purchase/    → screen-recorder
 *   /download                        → undefined
 *
 * Matching against the catalogue rather than reading a fixed segment position
 * is what makes the language prefix, trailing segments and trailing slashes all
 * irrelevant — an unknown segment simply never matches.
 */
export function findProductSlugInPath(
  pathname: string | undefined | null,
): string | undefined {
  if (!pathname) return undefined;
  for (const segment of pathname.split("/")) {
    if (PRODUCT_SLUG_SET.has(segment)) return segment;
  }
  return undefined;
}
